import { buildProperty, buildCollection, EntityReference } from "@firecms/core";
import enumLocales from "./locales.enum";
import uniqid from "uniqid";

interface ICustomOffer {
  custom_id: string;
  hidden: boolean;
  images: string[];
  video?: string;
  color: string;
  category_reference: EntityReference;
}

// Enum for custom offer types
const customOfferIds = {
  transportation: "Transportation",
  tailormade: "Tailor Made",
  birdwatching: "Birdwatching",
  accommodation: "Accommodation",
  accommodation_farm: "Accommodation Farm",
  ecolodge: "Ecolodge"
};

// Subcollection for localized content
const CustomOfferLocaleSchema = buildCollection({
  id: "custom_offer_locales",
  name: "Locale",
  path: "locales",
  customId: enumLocales,
  properties: {
    name: buildProperty({
      name: "Name",
      dataType: "string",
      validation: { required: true },
      description: "Short name displayed in category slider",
    }),
    title: buildProperty({
      name: "Title",
      dataType: "string",
      validation: { required: true },
      description: "Main title for the page",
    }),
    description: buildProperty({
      name: "Description",
      dataType: "string",
      validation: { required: true },
      description: "Short description/subtitle",
    }),
    mainDescription: buildProperty({
      name: "Main Description",
      dataType: "string",
      validation: { required: true },
      columnWidth: 400,
      description: "Longer description text",
      markdown: true,
    }),
    sectionTitle: buildProperty({
      name: "Section Title",
      dataType: "string",
      validation: { required: true },
      description: "Title for the features section",
    }),
    features: buildProperty({
      name: "Features",
      dataType: "array",
      validation: { required: true },
      description: "List of features/services offered",
      of: {
        dataType: "string",
      },
    }),
  },
});

// Main collection for custom offers
const CustomOfferSchema = buildCollection<ICustomOffer>({
  id: "custom_offers",
  name: "Custom Offers",
  path: "custom_offers",
  subcollections: [CustomOfferLocaleSchema],
  properties: {
    custom_id: buildProperty({
      name: "Custom ID",
      dataType: "string",
      validation: { required: true, unique: true },
      description: "Unique identifier for the custom offer",
      enumValues: customOfferIds,
    }),
    hidden: buildProperty({
      dataType: "boolean",
      name: "Hidden",
      defaultValue: false,
      validation: { required: true }
    }),
    category_reference: buildProperty({
      name: "Category",
      dataType: "reference",
      path: "categories",
      validation: { required: true },
      description: "The category tile that links to this offer page (one offer per category)",
      previewProperties: ["custom_id"],
    }),
    images: buildProperty({
      name: "Images",
      dataType: "array",
      validation: { required: true },
      description: "Images for the custom offer page",
      of: {
        dataType: "string",
        storage: {
          fileName: () => uniqid(),
          acceptedFiles: ["image/*"],
          storagePath: "images/custom_offers",
          metadata: {
            cacheControl: "max-age=1000000",
          },
          
        },
      },
    }),
    video: buildProperty({
      name: "Video",
      dataType: "string",
      validation: { required: false },
      description: "Video for the custom offer page",
      storage: {
        fileName: () => uniqid(),
        acceptedFiles: ["video/*"],
        storagePath: "videos/custom_offers",
        metadata: {
          cacheControl: "max-age=1000000",
        },
      },
    }),
    color: buildProperty({
      name: "Color",
      dataType: "string",
      validation: { required: true },
      description: "Color theme for the page",
      enumValues: {
        brown: "Brown",
        yellow: "Yellow",
        "yellow-darker": "Yellow Darker",
        blue: "Blue",
        green: "green",
      },
    }),
  },
});

export default CustomOfferSchema;
